import { useState, useEffect, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { RenoTicket, STATUS_COLORS } from '../types/renoTicket';
import { MagnifyingGlass, ArrowRight, WarningCircle } from '@phosphor-icons/react';

interface Props {
  isOpen: boolean;
  sourceTicket: RenoTicket;
  onClose: () => void;
  onMerged: (targetId: number) => void;
}

export function MergeRenoTicketModal({ isOpen, sourceTicket, onClose, onMerged }: Props) {
  const { session } = useSession();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<RenoTicket[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [target, setTarget] = useState<RenoTicket | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setResults([]);
      setTarget(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!search.trim() || !session) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const params = new URLSearchParams({ search: search.trim(), limit: '10' });
        const res = await fetchWithAuth(`${API_URL}/reno-helpdesk/tickets?${params}`, { method: 'GET' }, session);
        const data = await res.json();
        setResults((data.tickets ?? []).filter((t: RenoTicket) => t.id !== sourceTicket.id));
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, session, sourceTicket.id]);

  const handleMerge = async () => {
    if (!target || !session) return;
    setIsSubmitting(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/merge`,
        { method: 'POST', body: JSON.stringify({ sourceId: sourceTicket.id, targetId: target.id }), headers: { 'Content-Type': 'application/json' } },
        session,
      );
      if (!res.ok) throw new Error('Samenvoegen mislukt');
      onMerged(target.id);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Ticket RT-{sourceTicket.id} samenvoegen</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex gap-2 text-sm text-amber-800">
            <WarningCircle size={16} className="shrink-0 mt-0.5" weight="duotone" />
            <span>
              RT-{sourceTicket.id} wordt samengevoegd <strong>in</strong> het gekozen ticket. E-mails en actienotities worden overgezet. RT-{sourceTicket.id} wordt daarna verwijderd.
            </span>
          </div>

          {!target ? (
            <div className="space-y-2">
              <Label>Zoek het doelticket</Label>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Zoek op titel of RT-nummer..."
                  className="pl-8 text-sm"
                  autoFocus
                />
              </div>

              {isSearching && <p className="text-xs text-gray-500">Zoeken...</p>}

              {results.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100">
                  {results.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTarget(t)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-500">RT-{t.id}</span>
                        <span className="font-medium text-gray-900 truncate flex-1">{t.title}</span>
                        <span className={`shrink-0 inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[t.status]}`}>
                          {t.status}
                        </span>
                      </div>
                      {t.renovationProject && (
                        <p className="text-xs text-gray-500 mt-0.5 ml-10">{t.renovationProject.title}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {search.trim() && !isSearching && results.length === 0 && (
                <p className="text-xs text-gray-500">Geen tickets gevonden</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <Label>Samenvoegen</Label>
              <div className="flex items-center gap-3">
                <div className="flex-1 rounded-md border border-red-200 bg-red-50 p-3 text-sm">
                  <p className="text-xs text-gray-500 mb-1">Wordt verwijderd</p>
                  <p className="font-mono text-xs text-gray-500">RT-{sourceTicket.id}</p>
                  <p className="font-medium text-gray-900 truncate">{sourceTicket.title}</p>
                </div>
                <ArrowRight size={20} className="shrink-0 text-gray-400" />
                <div className="flex-1 rounded-md border border-green-200 bg-green-50 p-3 text-sm">
                  <p className="text-xs text-gray-500 mb-1">Blijft bestaan</p>
                  <p className="font-mono text-xs text-gray-500">RT-{target.id}</p>
                  <p className="font-medium text-gray-900 truncate">{target.title}</p>
                </div>
              </div>
              <button
                onClick={() => setTarget(null)}
                className="text-xs text-gray-500 underline hover:text-gray-700"
              >
                Ander ticket kiezen
              </button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Annuleren</Button>
          <Button
            onClick={handleMerge}
            disabled={!target || isSubmitting}
            variant="destructive"
          >
            {isSubmitting ? 'Bezig...' : 'Samenvoegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
